import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUsers, FiCheck, FiAlertCircle, FiTrash2 } from 'react-icons/fi';
import apiService from '../../services/api';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--border-color);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s;
  
  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const StudentList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-secondary);
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StudentName = styled.span`
  font-weight: 500;
  font-size: 0.875rem;
`;

const StudentEmail = styled.span`
  font-size: 0.75rem;
  opacity: 0.7;
`;

const RemoveButton = styled.button`
  background: var(--error-color);
  color: var(--text-inverse);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background: var(--error-dark);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background-color: var(--background-secondary);
      border-color: var(--border-dark);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  color: var(--error-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(104, 211, 145, 0.1);
  border: 1px solid var(--success-color);
  color: var(--success-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
`;

function RemoveElectiveStudentModal({ isOpen, onClose, elective, onStudentRemoved }) {
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingStudents, setRemovingStudents] = useState(new Set());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchEnrolledStudents = useCallback(async () => {
    if (!elective) return;
    
    try {
      setLoading(true);
      const response = await apiService.getElective(elective.id);
      if (response.success && response.elective.students) {
        setEnrolledStudents(response.elective.students);
      }
    } catch (error) {
      console.error('Error fetching enrolled students:', error);
      setError('Failed to fetch enrolled students');
    } finally {
      setLoading(false);
    }
  }, [elective]);

  useEffect(() => {
    if (isOpen) {
      fetchEnrolledStudents();
    }
  }, [isOpen, elective, fetchEnrolledStudents]);

  const handleRemoveStudent = async (studentId) => {
    try {
      setRemovingStudents(prev => new Set(prev).add(studentId));
      setError('');
      setSuccess('');

      const response = await apiService.removeStudentFromElective(elective.id, studentId);
      
      if (response.success) {
        setSuccess(`Student removed from elective successfully`);
        setEnrolledStudents(prev => prev.filter(student => student.id !== studentId));
        onStudentRemoved(studentId);
        
        // Clear success message after a delay
        setTimeout(() => {
          setSuccess('');
        }, 2000);
      } else {
        setError(response.error || 'Failed to remove student from elective');
      }
    } catch (error) {
      console.error('Error removing student:', error);
      setError('Failed to remove student from elective');
    } finally {
      setRemovingStudents(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setRemovingStudents(new Set());
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiUsers size={20} />
                Remove Students from Elective
              </ModalTitle>
              <CloseButton onClick={handleClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <Form>
              <FormGroup>
                <Label>Elective</Label>
                <div style={{ 
                  padding: '0.75rem', 
                  background: 'var(--bg-secondary)', 
                  borderRadius: 0,
                  border: '1px solid var(--border-color)'
                }}>
                  <strong>{elective?.code}</strong> - {elective?.name}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Enrolled Students ({enrolledStudents.length})</Label>
                
                {loading ? (
                  <div style={{ 
                    padding: '1rem', 
                    textAlign: 'center',
                    color: 'var(--text-secondary)'
                  }}>
                    <LoadingSpinner />
                    Loading students...
                  </div>
                ) : (
                  <StudentList>
                    {enrolledStudents.length === 0 ? (
                      <EmptyState>
                        <FiUsers size={32} />
                        <p>No students enrolled in this elective</p>
                      </EmptyState>
                    ) : (
                      enrolledStudents.map(student => {
                        const isRemoving = removingStudents.has(student.id);
                        return (
                          <StudentItem key={student.id}>
                            <StudentInfo>
                              <StudentName>
                                {student.first_name} {student.last_name}
                              </StudentName>
                              <StudentEmail>{student.email}</StudentEmail>
                            </StudentInfo>
                            <RemoveButton
                              onClick={() => handleRemoveStudent(student.id)}
                              disabled={isRemoving}
                            >
                              {isRemoving ? (
                                <>
                                  <LoadingSpinner />
                                  Removing...
                                </>
                              ) : (
                                <>
                                  <FiTrash2 size={12} />
                                  Remove
                                </>
                              )}
                            </RemoveButton>
                          </StudentItem>
                        );
                      })
                    )}
                  </StudentList>
                )}
              </FormGroup>

              {error && (
                <ErrorMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiAlertCircle size={16} />
                  {error}
                </ErrorMessage>
              )}

              {success && (
                <SuccessMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiCheck size={16} />
                  {success}
                </SuccessMessage>
              )}

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleClose}>
                  Close
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default RemoveElectiveStudentModal;

